import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNotification } from "../../context/NotificationContext";
import { BiographyPageSettingServices } from "../../services/admin/BiographyPageSettingServices";
import {
    Save,
    Quote,
    Edit2,
    X,
    Loader2,
    BookOpen,
    GraduationCap,
    Briefcase,
    Target,
    Layers,
    Image as ImageIcon,
    Upload
} from "lucide-react";

export default function BiographyPageSetting() {
    const { showNotification } = useNotification();
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        early_years: "",
        education_mentorship: "",
        professional_career: "",
        goal: "",
        current_projects: "",
        bio_quote: "",
        bio_image: null,
        bio_image_path: ""
    });

    const [originalData, setOriginalData] = useState({});
    const [imagePreview, setImagePreview] = useState(null);

    const fetchSettings = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await BiographyPageSettingServices.getSettings();
            if (response.status === "success") {
                setFormData({
                    ...response.data,
                    bio_image: null // Reset file object on load
                });
                setOriginalData(response.data);
                if (response.data.bio_image_path) {
                    setImagePreview(response.data.bio_image_path);
                }
            }
        } catch (error) {
            showNotification({
                type: "failed",
                title: "Load Error",
                message: "Failed to fetch biography settings."
            });
        } finally {
            setIsLoading(false);
        }
    }, [showNotification]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                showNotification({ type: "error", title: "File Too Large", message: "Image size should be less than 5MB." });
                return;
            }
            setFormData(prev => ({ ...prev, bio_image: file }));
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const getWordCount = (text) => {
        return text ? text.trim().split(/\s+/).length : 0;
    };

    const validate = () => {
        const { early_years, education_mentorship, professional_career, goal, current_projects, bio_quote, bio_image_path, bio_image } = formData;

        if (!early_years || !education_mentorship || !professional_career || !goal || !current_projects || !bio_quote) {
            showNotification({
                type: "error",
                title: "Validation Error",
                message: "All text sections and the biography quote are required."
            });
            return false;
        }

        if (getWordCount(goal) > 30) {
            showNotification({
                type: "error",
                title: "Validation Error",
                message: "The 'Personal Goal' section must not exceed 30 words."
            });
            return false;
        }

        if (getWordCount(bio_quote) > 30) {
            showNotification({
                type: "error",
                title: "Validation Error",
                message: "The 'Biography Page Quote' must not exceed 30 words."
            });
            return false;
        }

        if (!bio_image_path && !bio_image) {
            showNotification({
                type: "error",
                title: "Validation Error",
                message: "A biography image is required."
            });
            return false;
        }

        return true;
    };

    const handleSave = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        setIsSaving(true);
        try {
            const response = await BiographyPageSettingServices.updateSettings(formData);
            if (response.status === "success") {
                showNotification({ type: "success", title: "Settings Saved", message: "Biography settings updated successfully." });
                fetchSettings(); // Refresh to get updated paths
                setIsEditing(false);
            } else {
                showNotification({ type: "error", title: "Error", message: response.message || "Failed to update settings." });
            }
        } catch (error) {
            showNotification({ type: "error", title: "Error", message: error.message || "An unexpected error occurred." });
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setFormData({
            ...originalData,
            bio_image: null
        });
        setImagePreview(originalData.bio_image_path);
        setIsEditing(false);
    };

    const inputClasses = "w-full pl-12 pr-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-[#FAFAFA] dark:bg-[#222] text-sm focus:border-jazz-brown outline-none transition-colors disabled:opacity-75 disabled:cursor-not-allowed";
    const textareaClasses = `${inputClasses} pl-12 resize-none h-32`;
    const labelClasses = "block text-xs font-bold text-black dark:text-white uppercase tracking-widest mb-2";

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="animate-spin text-jazz-brown" size={48} />
            </div>
        );
    }

    return (
        <div className="p-6 lg:p-10 max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-10">
                <div>
                    <h1 className="text-3xl lg:text-4xl font-normal text-black dark:text-white mb-2" style={{ fontFamily: "Playfair Display, serif" }}>
                        Biography Page Settings
                    </h1>
                    <p className="text-black dark:text-white/50 text-sm font-serif">
                        Manage the detailed story, education, career highlights, and goals displayed on your biography page.
                    </p>
                </div>
                {!isEditing && (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center gap-2 px-6 py-2.5 bg-black text-white rounded-xl hover:bg-jazz-brown transition-colors text-xs font-bold tracking-widest uppercase"
                    >
                        <Edit2 size={16} />
                        Edit Biography
                    </button>
                )}
            </div>

            <div className="bg-white dark:bg-[#1A1A1A] p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                <form onSubmit={handleSave} className="space-y-10">

                    {/* Image Section */}
                    <div className="flex flex-col md:flex-row gap-8 items-start pb-10 border-b border-black/5 dark:border-white/5">
                        <div className="relative group">
                            <div className="w-48 h-64 rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800 border-2 border-dashed border-black/10 dark:border-white/10 flex items-center justify-center relative">
                                {imagePreview ? (
                                    <img src={imagePreview} alt="Biography Preview" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="text-center p-4">
                                        <ImageIcon className="mx-auto text-black/20 mb-2" size={32} />
                                        <p className="text-[10px] uppercase font-bold tracking-widest text-black/40">No Image Selected</p>
                                    </div>
                                )}
                                {isEditing && (
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current.click()}
                                        className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white gap-2"
                                    >
                                        <Upload size={20} />
                                        <span className="text-xs font-bold uppercase tracking-widest">Update</span>
                                    </button>
                                )}
                            </div>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleImageChange}
                                className="hidden"
                                accept="image/*"
                            />
                            <p className="mt-2 text-[10px] text-center text-black dark:text-white/40 uppercase tracking-widest">Biography Image <span className="text-red-500">*</span></p>
                        </div>

                        <div className="flex-1 w-full">
                            <div className="flex justify-between items-center mb-2">
                                <label className={labelClasses}>Biography Page Quote <span className="text-red-500">*</span></label>
                                <span className={`text-[10px] font-bold tracking-widest ${getWordCount(formData.bio_quote) > 30 ? 'text-red-500' : 'text-black/40'}`}>
                                    {getWordCount(formData.bio_quote)} / 30 WORDS
                                </span>
                            </div>
                            <div className="relative">
                                <Quote className="absolute left-4 top-4 text-black/40" size={18} />
                                <textarea
                                    name="bio_quote"
                                    value={formData.bio_quote || ""}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={`${textareaClasses} h-24`}
                                    placeholder="Enter a powerful opening quote for your biography page..."
                                />
                            </div>

                            <div className="mt-8">
                                <div className="flex justify-between items-center mb-2">
                                    <label className={labelClasses}>Personal Goal / MISSION <span className="text-red-500">*</span></label>
                                    <span className={`text-[10px] font-bold tracking-widest ${getWordCount(formData.goal) > 30 ? 'text-red-500' : 'text-black/40'}`}>
                                        {getWordCount(formData.goal)} / 30 WORDS
                                    </span>
                                </div>
                                <div className="relative">
                                    <Target className="absolute left-4 top-1/2 -translate-y-1/2 text-black/40" size={18} />
                                    <input
                                        type="text"
                                        name="goal"
                                        value={formData.goal || ""}
                                        onChange={handleChange}
                                        disabled={!isEditing}
                                        className={inputClasses}
                                        placeholder="Your core mission or goal (brief and punchy)..."
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Story Sections */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                            <label className={labelClasses}>The Early Years <span className="text-red-500">*</span></label>
                            <div className="relative">
                                <BookOpen className="absolute left-4 top-4 text-black/40" size={18} />
                                <textarea
                                    name="early_years"
                                    value={formData.early_years || ""}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={textareaClasses}
                                    placeholder="Describe your beginnings and first steps in music..."
                                />
                            </div>
                        </div>

                        <div>
                            <label className={labelClasses}>Education & Mentorship <span className="text-red-500">*</span></label>
                            <div className="relative">
                                <GraduationCap className="absolute left-4 top-4 text-black/40" size={18} />
                                <textarea
                                    name="education_mentorship"
                                    value={formData.education_mentorship || ""}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={textareaClasses}
                                    placeholder="Details about your academic background and mentors..."
                                />
                            </div>
                        </div>

                        <div>
                            <label className={labelClasses}>Professional Career <span className="text-red-500">*</span></label>
                            <div className="relative">
                                <Briefcase className="absolute left-4 top-4 text-black/40" size={18} />
                                <textarea
                                    name="professional_career"
                                    value={formData.professional_career || ""}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={textareaClasses}
                                    placeholder="Highlights from your professional journey, performances, and tours..."
                                />
                            </div>
                        </div>

                        <div>
                            <label className={labelClasses}>Current Projects <span className="text-red-500">*</span></label>
                            <div className="relative">
                                <Layers className="absolute left-4 top-4 text-black/40" size={18} />
                                <textarea
                                    name="current_projects"
                                    value={formData.current_projects || ""}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={textareaClasses}
                                    placeholder="What are you working on right now? Upcoming releases or collaborations..."
                                />
                            </div>
                        </div>
                    </div>

                    {isEditing && (
                        <div className="flex flex-col-reverse sm:flex-row justify-end gap-4 pt-6 mt-6 border-t border-black/5 dark:border-white/5">
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="flex items-center justify-center gap-2 px-8 py-3 bg-gray-100 text-black rounded-xl hover:bg-gray-200 transition-colors text-xs font-bold tracking-widest uppercase w-full sm:w-auto"
                            >
                                <X size={16} />
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving}
                                className="flex items-center justify-center gap-2 px-8 py-3 bg-jazz-brown text-white rounded-xl hover:bg-black transition-colors text-xs font-bold tracking-widest uppercase disabled:opacity-50 disabled:cursor-not-allowed shadow-lg w-full sm:w-auto"
                            >
                                {isSaving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save size={16} />
                                        Update Settings
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}
